import { graphql } from 'gatsby';
import type { RenderRichTextData } from 'gatsby-source-contentful/rich-text';
import React, { useMemo } from 'react';

import { Box, Container, Stack } from '@mui/material';
import { styled } from "@mui/material/styles";
import Typography from '@mui/material/Typography';
import {
  useContentfulInspectorMode, useContentfulLiveUpdates
} from '../shared/hooks/contentful';

import { notPrintable } from '../shared/styles/notPrintable';
import { renderContentfulRichText } from '../shared/utils';
import { ButtonList, ContentfulAsset, ContentfulAssetImage, HomepageLink, Kicker } from './ui';

export interface HeroProps {
  heroImage?: ContentfulAsset
  kicker?: string
  title: RenderRichTextData<null>
  subhead: string
  contentful_id: string
  text: string
  isFirstHero?: boolean
  links: HomepageLink[]
}

export default function Hero(props: HeroProps) {
  const inspectorProps = useContentfulInspectorMode();
  const data = useContentfulLiveUpdates({
    ...props,
    sys: { id: props.contentful_id },
  });

  const isFirstHero = data.isFirstHero

  // console.log('data', isFirstHero)

  const heroImage = data.heroImage && (
    <Box
      sx={{
        width: {
          xs: "290px",
          sm: "300px",
          md: "500px",
          lg: "700px",
          xl: "700px",
        }
      }}
    >
      <ContentfulAssetImage
        contentfulPreviewProps={inspectorProps({
          entryId: props.contentful_id,
          fieldId: 'image',
        })}
        aboveFold={isFirstHero}
        loading={isFirstHero ? "eager" : "lazy"}
        asset={data.heroImage}
      />
    </Box>
  )

  const NotPrintable = useMemo(() => styled(Container)(notPrintable), []);
  return (
    <NotPrintable sx={{ pt: { xs: 5, sm: 4, md: 3, lg: 4 } }}>
      <Stack
        direction={{ xs: "column-reverse", sm: "column-reverse", md: "row" }}
        gap={{ xs: 4, md: 3, lg: 5 }}
        alignItems="center"
        justifyContent={{
          sm: "center",
          md: "center",
          lg: "space-between",
        }}
        sx={{
          textAlign: {
            xs: "center",
            sm: "center",
            md: "inherit",
            lg: "inherit",
            xl: "inherit",
          },
          height: {
            xs: "auto",
            sm: "auto",
            md: "100%",
            lg: "100%",
            xl: "100%",
          },
          // p: { xs: 1, sm: 3, md: 2, lg: 4 },
          p: 0
          // pb: { xs: 2, sm: 2 },
          // pt: { xs: 2, sm: 4, md: 3, lg: 8 },
        }}
      >
        <Stack
          gap={{
            xs: 4,
            sm: 4,
            md: 4,
            lg: 4,
            xl: 4,
          }}
          sx={{ maxWidth: "550px" }}
          direction="column"
          justifyContent={{
            sm: "flex-start",
            md: "flex-start",
            lg: "space-between",
          }}
        >
          {data.kicker && <Kicker label={data.kicker} />}

          {data.title &&
            <Typography
              variant="h1"
              sx={{
                color: "inherit",
                marginBottom: "-1rem"
                // pt: { xs: "120px", sm: "80px", md: 0 },
              }}
              {...inspectorProps({
                entryId: props.contentful_id,
                fieldId: 'title',
              })}
            >
              {renderContentfulRichText(data.title)}
            </Typography>
          }

          {data.subhead ? (
            <Box sx={{ maxWidth: "500px" }}>
              <Typography
                variant="h1"
                component={"h1"}
                {...inspectorProps({
                  entryId: props.contentful_id,
                  fieldId: 'subhead',
                })}
                sx={{ color: "inherit", lineHeight: "2.8rem" }}
              >
                {data.subhead}
              </Typography>
            </Box>
          ) : (
            <></>
          )}
          {data.text ? (
            <Typography
              variant="h4"
              component={"h1"}
              sx={{
                color: "inherit",
                fontWeight: "500",
                opacity: "0.7",
                whiteSpace: "pre-line",
              }}
              {...inspectorProps({
                entryId: props.contentful_id,
                fieldId: 'text',
              })}
            >
              {data.text}
            </Typography>
          ) : (
            <></>
          )}

          {data.links && data.links.length > 0 ? (
            <div>
              <ButtonList links={data.links} />
            </div>
          ) : (
            <></>
          )}
        </Stack>

        {heroImage}
      </Stack>
    </NotPrintable>
  )
}

export const query = graphql`
  fragment HomepageHeroContent on ContentfulHomepageHero {
    id
    title {
      raw
    }
    # subhead
    text
    __typename
    contentful_id   
    links {
      id
      href
      text
      __typename
      contentful_id
    }
    heroImage: image {
      id
      contentful_id
      __typename
      localFile {
        childImageSharp {          
          gatsbyImageData(
            placeholder: NONE
            quality: 80
            width: 480
            breakpoints: [300, 450]
            layout: CONSTRAINED
          )
        }
      }
      title
    }
  }
`
